import { Profiler, ProfilerOnRenderCallback, ReactNode } from 'react';
import log from 'common/ScalyrLogger';

/**
 * A wrapper component around React Profiler and logs an object that describes a render.
 * Reference - https://reactjs.org/docs/profiler.html
 *
 * @param {Fragment} children   The children component tree.
 * @param {string}   id         Required. The id of the profiler.
 * @param {object}   extraJson  Optional. Additional info to log to server, which can be anything.
 */

type ScalyrProfilerProps = {
  children?: ReactNode;
  id: string;
  // type "any" makes sense here because it could be an arbitrary JSON
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraJson?: any;
};

const ScalyrProfiler = ({ children, id, extraJson }: ScalyrProfilerProps) => {
  /**
   * The callback function for Profiler.
   * Explanations of the parameters - https://reactjs.org/docs/profiler.html#onrender-callback
   */
  const onRenderCallback: ProfilerOnRenderCallback = (
    profilerId, // the "id" prop of the Profiler tree that has just committed
    phase, // either "mount" (if the tree just mounted) or "update" (if it re-rendered)
    actualDuration, // time spent rendering the committed update
    baseDuration, // estimated time to render the entire subtree without memoization
    startTime, // when React began rendering this update
    commitTime, // when React committed this update
    interactions, // the Set of interactions belonging to this update
  ) => {
    // we only care about phase 'mount', the first render, for now.
    if (phase === 'update') {
      return;
    }
    const objectToLog = {
      profilerId,
      phase,
      actualDuration,
      baseDuration,
      startTime,
      commitTime,
      interactions,
      ...extraJson,
    };
    log.info(objectToLog);
  };

  return (
    <Profiler id={id} onRender={onRenderCallback}>
      {children}
    </Profiler>
  );
};

export default ScalyrProfiler;
